import React, { useEffect, useMemo, useState } from "react";
import {
  getFeedbacks,
  updateFeedback,
  addDiscussion,
  getLearnersById,
  getAdminById,
  getFeedbackById,
} from "../api";
import axios from "axios";
import "./feedback.css";

const API_BASE_URL = "https://care-api.equily.ng/api";

const getAuthHeaders = () => {
  const token = localStorage.getItem("authToken");
  return {
    Authorization: `Bearer ${token}`,
    Accept: "application/json",
    "Content-Type": "application/json",
  };
};

const getSignedInAdminId = () => {
  const authUserRaw = localStorage.getItem("authUser");
  if (!authUserRaw) return null;

  try {
    const authUser = JSON.parse(authUserRaw);
    return authUser?.id || authUser?.admin_id || authUser?.data?.id || null;
  } catch (err) {
    return null;
  }
};

const STATUS_OPTIONS = ["New", "In Progress", "Resolved", "Closed"];
const TYPE_OPTIONS = ["Feedback", "Complaint", "Bug", "Suggestion"];
const PRIORITY_OPTIONS = ["Low", "Medium", "High", "Urgent"];

const parseDateValue = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const formatDateTime = (value) => {
  const parsed = parseDateValue(value);
  return parsed ? parsed.toLocaleString() : "N/A";
};

const getActivityTimestamp = (item) => {
  const updated = parseDateValue(item.updated_at)?.getTime();
  const created = parseDateValue(item.created_at)?.getTime();
  return updated ?? created ?? 0;
};

const normalizeConversations = (conversations = []) =>
  (Array.isArray(conversations) ? conversations : [])
    .map((entry) => {
      const text =
        entry?.text ||
        entry?.body ||
        entry?.message ||
        entry?.response ||
        entry?.reply ||
        entry?.content ||
        "";
      const isAdminMessage =
        Boolean(entry?.is_admin) ||
        Boolean(entry?.admin_id) ||
        Boolean(entry?.admin);
      const by =
        entry?.by ||
        entry?.sender_name ||
        entry?.staff?.full_name ||
        entry?.author?.name ||
        entry?.user?.full_name ||
        entry?.admin?.full_name ||
        (isAdminMessage ? "Admin" : "User");
      const at = entry?.at || entry?.created_at || entry?.updated_at || null;
      const attachments = Array.isArray(entry?.attachments)
        ? entry.attachments
        : [];

      if (!text && attachments.length === 0) return null;

      return {
        by,
        at,
        text,
        body: entry?.body || "",
        attachments,
        senderType: isAdminMessage ? "admin" : "user",
      };
    })
    .filter(Boolean);

const pickFirstNonEmptyArray = (...candidates) => {
  const normalizeCandidate = (candidate) => {
    if (Array.isArray(candidate)) return candidate;
    if (Array.isArray(candidate?.data)) return candidate.data;
    if (Array.isArray(candidate?.items)) return candidate.items;
    return null;
  };

  for (const candidate of candidates) {
    const normalizedCandidate = normalizeCandidate(candidate);
    if (normalizedCandidate && normalizedCandidate.length > 0) {
      return normalizedCandidate;
    }
  }

  for (const candidate of candidates) {
    const normalizedCandidate = normalizeCandidate(candidate);
    if (normalizedCandidate) {
      return normalizedCandidate;
    }
  }

  return [];
};

const getFeedbackRootMessage = (feedback) => {
  if (!feedback) return null;

  const rootText =
    feedback?.body || feedback?.summary || feedback?.subject || "";

  if (!String(rootText).trim()) return null;

  const requesterName =
    feedback?.requesterDetails?.name ||
    feedback?.requester ||
    feedback?.user?.full_name ||
    feedback?.staff?.full_name ||
    "User";

  return {
    by: requesterName,
    at: feedback?.created_at || feedback?.updated_at || null,
    text: String(rootText).trim(),
    body: String(rootText).trim(),
    attachments: [],
    senderType: "user",
  };
};

const getFeedbackThread = (feedback) => {
  const normalized = normalizeConversations(
    pickFirstNonEmptyArray(
      feedback?.messages,
      feedback?.responses,
      feedback?.response,
      feedback?.feedback_responses,
      feedback?.conversations,
      feedback?.data?.responses,
      feedback?.data?.response,
      feedback?.data?.feedback_responses,
      feedback?.data?.conversations,
    ),
  );

  const rootMessage = getFeedbackRootMessage(feedback);
  if (!rootMessage) return normalized;

  const alreadyHasRoot = normalized.some(
    (msg) => String(msg?.text || msg?.body || "").trim() === rootMessage.text,
  );

  return alreadyHasRoot ? normalized : [rootMessage, ...normalized];
};

const normalizeFeedbackItem = async (f) => {
  let user = f.user || f.staff || f.admin || f.learner || {};
  let userDetails = null;
  const userId =
    f.staff_id || f.admin_id || f.user_id || f.learner_id || user?.id || null;

  if (userId) {
    try {
      try {
        userDetails = await getLearnersById(userId);
        if (userDetails && userDetails.id) {
          user = userDetails;
        }
      } catch (learnerErr) {
        try {
          userDetails = await getAdminById(userId);
          if (userDetails && userDetails.id) {
            user = userDetails;
          }
        } catch (adminErr) {
          try {
            const staffRes = await axios.get(`${API_BASE_URL}/staff/${userId}`, {
              headers: getAuthHeaders(),
            });
            userDetails = staffRes.data?.data || staffRes.data;
            if (userDetails && (userDetails.id || userDetails.staff_id)) {
              user = userDetails;
            }
          } catch (staffErr) {
            // Keep original user data if available
          }
        }
      }
    } catch (err) {
      // Keep original user data if fetching fails
    }
  }

  const finalUserId =
    user?.id ||
    user?.learner_id ||
    user?.staff_id ||
    f.staff_id ||
    f.admin_id ||
    f.user_id ||
    f.learner_id ||
    null;

  let userName =
    user?.full_name ||
    user?.name ||
    (user?.firstname && user?.lastname
      ? `${user.firstname} ${user.lastname}`.trim()
      : null) ||
    (user?.first_name && user?.last_name
      ? `${user.first_name} ${user.last_name}`.trim()
      : null) ||
    (user?.firstname ? user.firstname : null) ||
    (user?.lastname ? user.lastname : null);

  if (!userName) {
    userName =
      user?.email ||
      user?.phone_number ||
      user?.phone ||
      (userId ? `User #${userId}` : null) ||
      "Anonymous";
  }

  const userPhone = user?.phone_number || user?.phone || user?.phoneNumber || "N/A";

  const userLGA =
    user?.lga?.name ||
    user?.lga_name ||
    user?.local_government ||
    user?.local_government_area ||
    (typeof user?.lga === "string" ? user.lga : null) ||
    "N/A";

  const userFacility =
    user?.facility?.name ||
    user?.facility_name ||
    user?.facilityName ||
    (typeof user?.facility === "string" ? user.facility : null) ||
    "N/A";

  const userEmail = user?.email || "N/A";

  return {
    id: f.id,
    subject: f.subject || f.body || "No subject",
    requester: userName,
    requesterDetails: {
      name: userName,
      phone: userPhone,
      lga: userLGA,
      facility: userFacility,
      email: userEmail,
      userId: finalUserId,
    },
    type: f.type || "Feedback",
    priority: f.priority || "Medium",
    status: f.status || "New",
    created_at: f.created_at,
    updated_at: f.updated_at,
    summary: f.summary || f.body || "",
    messages: getFeedbackThread(f),
    responses: f.responses,
    response: f.response,
    feedback_responses: f.feedback_responses,
    conversations: f.conversations,
    data: f.data,
    responses_count:
      f.responses_count || (Array.isArray(f.responses) ? f.responses.length : 0),
    tags: f.tags || [],
    attachments: f.attachments || [],
  };
};

const fetchAndNormalizeFeedbacks = async () => {
  const res = await getFeedbacks();
  const feedbacks = res.data?.data || res.data || [];
  return Promise.all(feedbacks.map((f) => normalizeFeedbackItem(f)));
};

const Badge = ({ className = "", children }) => (
  <span className={`badge ${className}`}>{children}</span>
);

export default function Feedback() {
  const [data, setData] = useState([]);
  const [selected, setSelected] = useState([]);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [typeFilter, setTypeFilter] = useState("All");
  const [priorityFilter, setPriorityFilter] = useState("All");
  const [modalOpen, setModalOpen] = useState(false);
  const [active, setActive] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replySuccess, setReplySuccess] = useState(false);
  const [sendingReply, setSendingReply] = useState(false);
  const [replyError, setReplyError] = useState("");
  const [refreshingActive, setRefreshingActive] = useState(false);

  // ✅ Load feedbacks and fetch user details
  useEffect(() => {
    (async () => {
      try {
        const processedFeedbacks = await fetchAndNormalizeFeedbacks();

        setData(processedFeedbacks);
      } catch (err) {
        // Error handled silently
      }
    })();
  }, []);

  // ✅ Status update
  const changeStatus = async (id, status) => {
    await updateFeedback(id, status);
    setData((arr) =>
      arr.map((x) =>
        x.id === id
          ? { ...x, status, updated_at: new Date().toISOString() }
          : x,
      ),
    );
    if (active?.id === id) setActive((a) => ({ ...a, status }));
  };

  const bulkSetStatus = async (status) => {
    if (selected.length === 0) return;
    await Promise.all(selected.map((id) => updateFeedback(id, status)));
    setData((arr) =>
      arr.map((x) => (selected.includes(x.id) ? { ...x, status } : x)),
    );
    setSelected([]);
  };

  // ✅ Filters
  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    return data.filter((item) => {
      const itemId = String(item.id || "").toLowerCase();
      const subject = String(item.subject || "").toLowerCase();
      const requester = String(item.requester || "").toLowerCase();
      const summary = String(item.summary || "").toLowerCase();
      const matchesSearch =
        !s ||
        itemId.includes(s) ||
        subject.includes(s) ||
        requester.includes(s) ||
        summary.includes(s) ||
        (item.tags || []).some((t) =>
          String(t || "")
            .toLowerCase()
            .includes(s),
        );

      const matchesStatus =
        statusFilter === "All" || item.status === statusFilter;
      const matchesType = typeFilter === "All" || item.type === typeFilter;
      const matchesPriority =
        priorityFilter === "All" || item.priority === priorityFilter;

      return matchesSearch && matchesStatus && matchesType && matchesPriority;
    });
  }, [data, search, statusFilter, typeFilter, priorityFilter]);

  const sortedFeedback = useMemo(
    () =>
      [...filtered].sort(
        (a, b) => getActivityTimestamp(b) - getActivityTimestamp(a),
      ),
    [filtered],
  );

  const toggleRow = (id) =>
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const allVisibleSelected =
    filtered.length > 0 && filtered.every((f) => selected.includes(f.id));

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelected((prev) =>
        prev.filter((id) => !filtered.find((f) => f.id === id)),
      );
    } else {
      const visibleIds = filtered.map((f) => f.id);
      setSelected((prev) => Array.from(new Set([...prev, ...visibleIds])));
    }
  };

  const activeResponses = pickFirstNonEmptyArray(
    active?.responses,
    active?.response,
    active?.feedback_responses,
    active?.conversations,
    active?.data?.responses,
    active?.data?.response,
    active?.data?.feedback_responses,
    active?.data?.conversations,
  );
  const signedInAdminId = getSignedInAdminId();

  const openModal = async (item) => {
    const fullItem = data.find((d) => d.id === item.id) || item;
    // Show the cached copy immediately so the modal doesn't open empty,
    // then replace it with a fresh fetch below (latest status + responses).
    setActive({
      ...fullItem,
      messages: getFeedbackThread(fullItem),
    });
    setModalOpen(true);
    setReplyText("");
    setReplyError("");

    setRefreshingActive(true);
    try {
      const res = await getFeedbackById(item.id);
      const fresh = res?.data?.data || res?.data;
      if (fresh && fresh.id) {
        const merged = {
          ...fullItem,
          status: fresh.status || fullItem.status,
          priority: fresh.priority || fullItem.priority,
          type: fresh.type || fullItem.type,
          summary: fresh.body || fresh.summary || fullItem.summary,
          responses: fresh.responses,
          response: fresh.response,
          feedback_responses: fresh.feedback_responses,
          conversations: fresh.conversations,
          data: fresh.data,
          responses_count:
            fresh.responses_count ??
            (Array.isArray(fresh.responses) ? fresh.responses.length : fullItem.responses_count),
        };
        setActive((prev) =>
          prev && prev.id === item.id
            ? { ...merged, messages: getFeedbackThread(merged) }
            : prev,
        );
        setData((arr) => arr.map((x) => (x.id === item.id ? { ...x, ...merged } : x)));
      }
    } catch (err) {
      // Keep showing the cached copy already displayed above.
    } finally {
      setRefreshingActive(false);
    }
  };

  // ✅ return your same JSX UI
  return (
    <div className="feedback-page">
      <div className="feedback-header">
        <div className="title-wrap">
          <h1>Feedback</h1>
          <p className="subtitle">
            Manage feedback, complaints, and suggestions
          </p>
        </div>
      </div>

      <div className="toolbar">
        <div className="search">
          <input
            type="text"
            className="search-input"
            placeholder="Search by subject, requester, tag, or ID…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <span className="search-icon">⌕</span>
        </div>

        <div className="filters">
          <select
            className="filter"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option>All</option>
            {STATUS_OPTIONS.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>

          <select
            className="filter"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
          >
            <option>All</option>
            {TYPE_OPTIONS.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>

          <select
            className="filter"
            value={priorityFilter}
            onChange={(e) => setPriorityFilter(e.target.value)}
          >
            <option>All</option>
            {PRIORITY_OPTIONS.map((s) => (
              <option key={s}>{s}</option>
            ))}
          </select>
        </div>

        <div className="bulk-actions">
          <button
            className="btn btn-outline"
            disabled={selected.length === 0}
            onClick={() => bulkSetStatus("In Progress")}
          >
            Mark In Progress
          </button>
          <button
            className="btn btn-outline"
            disabled={selected.length === 0}
            onClick={() => bulkSetStatus("Resolved")}
          >
            Mark Resolved
          </button>
          <button
            className="btn btn-outline"
            disabled={selected.length === 0}
            onClick={() => bulkSetStatus("Closed")}
          >
            Close
          </button>
        </div>
      </div>

      <div className="card">
        <div className="fb-table-wrap">
          <table className="fb-table">
            <thead>
              <tr>
                <th className="w-40">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAll}
                    className="accent"
                    aria-label="Select all"
                  />
                </th>
                {/* <th>Subject</th> */}
                <th className="w-160">Requester</th>
                <th className="w-140">Type</th>
                {/* <th className="w-140">Priority</th> */}
                <th className="w-160">Conversations</th>
                <th className="w-180">Created</th>
                <th className="w-40"></th>
              </tr>
            </thead>

            <tbody>
              {sortedFeedback.map((item) => {
                const isSelected = selected.includes(item.id);
                return (
                  <tr
                    key={item.id}
                    onClick={() => toggleRow(item.id)}
                    className={isSelected ? "selected" : ""}
                  >
                    <td onClick={(e) => e.stopPropagation()}>
                      <input
                        type="checkbox"
                        className="accent"
                        checked={isSelected}
                        onChange={() => toggleRow(item.id)}
                        aria-label={`Select ${item.id}`}
                      />
                    </td>
                    {/* <td className="subject-cell">
                  <div className="subject">
                    <span className="id">{item.id}</span>
                    <span className="text">{item.subject}</span>
                    {item.tags?.length > 0 && (
                      <span className="tags">
                        {item.tags.slice(0, 2).map((t) => (
                          <span key={t} className="tag">
                            {t}
                          </span>
                        ))}
                        {item.tags.length > 2 && (
                          <span className="tag more">
                            +{item.tags.length - 2}
                          </span>
                        )}
                      </span>
                    )}
                  </div>
                  <div className="subline">{item.summary || "No additional summary provided."}</div>
                </td> */}
                    <td>
                      <div className="requester-cell">
                        <div className="requester-name">{item.requester}</div>
                        {item.requesterDetails &&
                          item.requesterDetails.phone !== "N/A" && (
                            <div className="requester-meta">
                              {item.requesterDetails.phone}
                            </div>
                          )}
                      </div>
                    </td>
                    <td>
                      <Badge className={`type ${item.type.toLowerCase()}`}>
                        {item.type}
                      </Badge>
                    </td>
                    {/* <td>
                      <Badge
                        className={`priority ${item.priority.toLowerCase()}`}
                      >
                        {item.priority}
                      </Badge>
                    </td> */}

                    <td>
                      {" "}
                      <Badge
                        className={`priority ${item.priority.toLowerCase()}`}
                      >
                        {item.responses_count}
                      </Badge>{" "}
                      Responses
                    </td>
                    {/* <td onClick={(e) => e.stopPropagation()}>
                  <select
                    className="status-select"
                    value={item.status}
                    onChange={(e) => changeStatus(item.id, e.target.value)}
                  >
                    {STATUS_OPTIONS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                </td> */}
                    <td>
                      {formatDateTime(item.created_at)}
                    </td>
                    <td
                      onClick={(e) => {
                        e.stopPropagation();
                        openModal(item);
                      }}
                    >
                      <button
                        className="btn btn-ghost view-btn"
                        title="View details"
                      >
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M12 4.5C7 4.5 2.73 7.61 1 12c1.73 4.39 6 7.5 11 7.5s9.27-3.11 11-7.5c-1.73-4.39-6-7.5-11-7.5zM12 17c-2.76 0-5-2.24-5-5s2.24-5 5-5 5 2.24 5 5-2.24 5-5 5zm0-8c-1.66 0-3 1.34-3 3s1.34 3 3 3 3-1.34 3-3-1.34-3-3-3z" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                );
              })}
              {sortedFeedback.length === 0 && (
                <tr>
                  <td colSpan="8">
                    <div className="empty">
                      No feedback found. Adjust your filters or try a different
                      search.
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Professional Modal */}
      {modalOpen && active && (
        <div
          className="feedback-modal-overlay"
          onClick={() => setModalOpen(false)}
        >
          <div className="feedback-modal" onClick={(e) => e.stopPropagation()}>
            {/* Modal Header */}
            <div className="feedback-modal-header">
              <div className="feedback-modal-title-section">
                <h2>{active.type ? `${active.type} #${active.id}` : "Feedback Details"}</h2>
                <div className="feedback-modal-badges">
                  <Badge
                    className={`status ${active.status.replace(/\s/g, "").toLowerCase()}`}
                  >
                    {active.status}
                  </Badge>
                  <Badge className={`type ${active.type?.toLowerCase()}`}>
                    {active.type}
                  </Badge>
                  <Badge
                    className={`priority ${active.priority?.toLowerCase()}`}
                  >
                    {active.priority}
                  </Badge>
                </div>
              </div>
              <button
                className="feedback-modal-close"
                onClick={() => setModalOpen(false)}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                >
                  <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
                </svg>
              </button>
            </div>

            {/* Modal Body */}
            <div className="feedback-modal-body">
              {/* User Information Section */}
              <div className="feedback-user-section">
                <div className="feedback-user-header">
                  <div className="feedback-user-avatar">
                    <svg
                      width="32"
                      height="32"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                    </svg>
                  </div>
                  <div className="feedback-user-info">
                    <h3>
                      {active.requesterDetails?.name &&
                      active.requesterDetails.name !== "Anonymous"
                        ? active.requesterDetails.name
                        : active.requester && active.requester !== "Anonymous"
                          ? active.requester
                          : "Anonymous User"}
                    </h3>
                    <p className="feedback-user-id">
                      Feedback ID: #{active.id}
                      {active.requesterDetails?.userId && (
                        <span>
                          {" "}
                          | User ID: #{active.requesterDetails.userId}
                        </span>
                      )}
                    </p>
                    {active.created_at && (
                      <p className="feedback-user-date">
                        Submitted: {formatDateTime(active.created_at)}
                      </p>
                    )}
                  </div>
                </div>

                {/* Always show user details section, even if some fields are missing */}
                <div className="feedback-user-details">
                  <div className="feedback-detail-item">
                    <div className="feedback-detail-icon">
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M6.62 10.79c1.44 2.83 3.76 5.14 6.59 6.59l2.2-2.2c.27-.27.67-.36 1.02-.24 1.12.37 2.33.57 3.57.57.55 0 1 .45 1 1V20c0 .55-.45 1-1 1-9.39 0-17-7.61-17-17 0-.55.45-1 1-1h3.5c.55 0 1 .45 1 1 0 1.25.2 2.45.57 3.57.11.35.03.74-.25 1.02l-2.2 2.2z" />
                      </svg>
                    </div>
                    <div className="feedback-detail-content">
                      <span className="feedback-detail-label">
                        Phone Number
                      </span>
                      <span className="feedback-detail-value">
                        {active.requesterDetails?.phone &&
                        active.requesterDetails.phone !== "N/A"
                          ? active.requesterDetails.phone
                          : "Not available"}
                      </span>
                    </div>
                  </div>

                  <div className="feedback-detail-item">
                    <div className="feedback-detail-icon">
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z" />
                      </svg>
                    </div>
                    <div className="feedback-detail-content">
                      <span className="feedback-detail-label">
                        Email Address
                      </span>
                      <span className="feedback-detail-value">
                        {active.requesterDetails?.email &&
                        active.requesterDetails.email !== "N/A"
                          ? active.requesterDetails.email
                          : "Not available"}
                      </span>
                    </div>
                  </div>

                  <div className="feedback-detail-item">
                    <div className="feedback-detail-icon">
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
                      </svg>
                    </div>
                    <div className="feedback-detail-content">
                      <span className="feedback-detail-label">
                        Local Government Area
                      </span>
                      <span className="feedback-detail-value">
                        {active.requesterDetails?.lga &&
                        active.requesterDetails.lga !== "N/A"
                          ? active.requesterDetails.lga
                          : "Not available"}
                      </span>
                    </div>
                  </div>

                  <div className="feedback-detail-item">
                    <div className="feedback-detail-icon">
                      <svg
                        width="18"
                        height="18"
                        viewBox="0 0 24 24"
                        fill="currentColor"
                      >
                        <path d="M12 2L2 7v10c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V7l-10-5z" />
                      </svg>
                    </div>
                    <div className="feedback-detail-content">
                      <span className="feedback-detail-label">Facility</span>
                      <span className="feedback-detail-value">
                        {active.requesterDetails?.facility &&
                        active.requesterDetails.facility !== "N/A"
                          ? active.requesterDetails.facility
                          : "Not available"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Original Feedback Message */}
              <div className="feedback-body-section">
                <h4>Feedback Message</h4>
                <div className="feedback-body-text">
                  {active.summary || active.subject || "No message provided."}
                </div>
              </div>

              {/* Status Actions */}
              <div className="feedback-status-actions">
                <label className="feedback-status-label">Update Status:</label>
                <div className="feedback-status-buttons">
                  {STATUS_OPTIONS.map((s) => (
                    <button
                      key={s}
                      className={`feedback-status-btn ${
                        active.status === s ? "active" : ""
                      }`}
                      onClick={() => {
                        changeStatus(active.id, s);
                        setActive({ ...active, status: s });
                      }}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>

              {/* Conversation Section */}
              <div className="feedback-conversation-section">
                <h4>
                  Conversation
                  {refreshingActive && (
                    <span className="feedback-refreshing-indicator">
                      <span className="feedback-refreshing-spinner" aria-hidden="true" />
                      Refreshing...
                    </span>
                  )}
                </h4>
                {activeResponses.length > 0 && (
                  <div className="feedback-raw-responses">
                    {activeResponses.map((responseItem, index) => (
                      <div
                        key={responseItem?.id || responseItem?.uuid || index}
                        className="feedback-raw-response-item"
                      >
                        <div className="feedback-raw-response-header">
                          <div className="feedback-raw-response-user">
                           
                            <span className="feedback-raw-response-role">
                               {responseItem.user?.full_name ||
                                "Anonymous User"}
                            </span>

                              <span className="feedback-raw-response-role">
                               {responseItem.user?.type}
                            </span>
                            
                          </div>
                          <span className="feedback-raw-response-time">
                            {formatDateTime(
                              responseItem?.created_at ||
                                responseItem?.updated_at ||
                                active?.created_at,
                            )}
                          </span>
                        </div>
                        <div className="feedback-raw-response-body">
                          {responseItem?.body ||
                            responseItem?.text ||
                            responseItem?.message ||
                            responseItem?.response ||
                            "No response body"}
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Reply Section */}
                <div className="feedback-reply-section">
                 

                  {replySuccess && (
                    <div className="feedback-reply-success">
                      ✓ Reply sent successfully.
                    </div>
                  )}
                  {replyError && (
                    <div className="feedback-reply-error">{replyError}</div>
                  )}
                  <textarea
                    className="feedback-reply-textarea mt-6"
                    rows="4"
                    placeholder="Type your reply…"
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    disabled={sendingReply}
                  />
                  <div className="feedback-reply-actions">
                    <button
                      className="btn btn-primary feedback-send-btn"
                      onClick={async () => {
                        if (!active || !replyText.trim() || sendingReply) return;

                        const feedbackId = active?.id || null;
                        const adminId =
                          signedInAdminId ||
                          active?.admin_id ||
                          active?.staff_id ||
                          null;

                        if (!feedbackId || !adminId) {
                          return;
                        }

                        setSendingReply(true);
                        setReplyError("");

                        try {
                          await addDiscussion(0, {
                            feedback_id: feedbackId,
                            admin_id: adminId,
                            body: replyText.trim(),
                          });

                          const refreshedFeedbacks =
                            await fetchAndNormalizeFeedbacks();
                          setData(refreshedFeedbacks);

                          const refreshedActive = refreshedFeedbacks.find(
                            (item) => item.id === feedbackId,
                          );
                          if (refreshedActive) {
                            setActive(refreshedActive);
                          }

                          setReplyText("");
                          setReplySuccess(true);
                          setTimeout(() => setReplySuccess(false), 4000);
                        } catch (err) {
                          setReplyError("Failed to send reply. Please try again.");
                        } finally {
                          setSendingReply(false);
                        }
                      }}
                      disabled={
                        !active || !replyText.trim() || sendingReply
                      }
                    >
                      {sendingReply ? (
                        <span className="feedback-send-spinner" aria-hidden="true" />
                      ) : (
                        <svg
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="currentColor"
                        >
                          <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                        </svg>
                      )}
                      {sendingReply ? "Sending..." : "Send Reply"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
